import React, { useState, useEffect } from 'react';
import { useCommonTranslation } from '../../../hooks/useCommonTranslation';

import {
  Box,
  Button,
  TextField,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Card,
  CardContent,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Search as SearchIcon } from '@mui/icons-material';

import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
const PREFIX = 'ActivityTab';

const classes = {
  container: `${PREFIX}-container`,
  searchBox: `${PREFIX}-searchBox`,
  addTaskButton: `${PREFIX}-addTaskButton`,
  typography: `${PREFIX}-typography`,
  timeText: `${PREFIX}-timeText`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'stretch',
    padding: theme.spacing(2),
  },
  [`& .${classes.searchBox}`]: {
    marginBottom: theme.spacing(2),
    marginRight: theme.spacing(2),
    width: '40%',
  },
  [`& .${classes.addTaskButton}`]: {
    marginRight: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
  },

  [`& .${classes.typography}`]: {
    fontFamily: 'Source Sans Pro',
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: '400',
    lineHeight: '143%',
    letterSpacing: '0.17px',
    wordWrap: 'break-word',
  },

  [`& .${classes.timeText}`]: {
    paddingLeft: theme.spacing(2),
  },
}));

interface Message {
  id: string; // Changed to string to match snsMessageID
  primary: string;
  secondary: string;
  detail?: string;
}

export default function TripDetailActivityTab() {
  const t = useCommonTranslation();
  const dispatch = useAppDispatch();

  const { messages: rawMessages, loading } = useAppSelector(
    (state) => state.message,
  );

  // State to store transformed messages
  const [messages, setMessages] = useState<Message[]>([]);

  // Transform rawMessages to match the structure of initialMessages
  useEffect(() => {
    if (rawMessages) {
      const transformedMessages = rawMessages.map((msg) => ({
        id: msg.snsMessageID,
        primary: msg.smsStatus,
        secondary: new Date(msg.createdOnUTC).toLocaleString(), // Convert UTC string to locale string
        detail: msg.smsMessage,
      }));
      setMessages(transformedMessages);
    }
  }, [rawMessages]);

  const [searchTerm, setSearchTerm] = useState('');
  const [open, setOpen] = useState(false);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setSearchTerm(value);
    if (!value) {
      // Reset messages if search term is cleared
      setMessages(
        rawMessages.map((msg) => ({
          id: msg.snsMessageID,
          primary: msg.smsStatus,
          secondary: new Date(msg.createdOnUTC).toLocaleString(),
          detail: msg.smsMessage,
        })),
      );
    } else {
      const lowercasedValue = value.toLowerCase();
      const filteredMessages = messages.filter(
        (message) =>
          message.primary.toLowerCase().includes(lowercasedValue) ||
          message.detail?.toLowerCase().includes(lowercasedValue) ||
          message.secondary.toLowerCase().includes(lowercasedValue),
      );
      setMessages(filteredMessages);
    }
  };

  const handleAddTaskClick = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <StyledBox className={classes.container}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <TextField
          id="search-messages"
          type="search"
          variant="outlined"
          size="small"
          placeholder="ID, message, type, etc."
          value={searchTerm}
          onChange={handleSearchChange}
          className={classes.searchBox}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <Button
          variant="outlined"
          onClick={handleAddTaskClick}
          className={classes.addTaskButton}>
          + Task
        </Button>
      </Box>
      <Card>
        <CardContent>
          <List sx={{ pt: 0 }}>
            {messages.map((message) => (
              <ListItem key={message.id} alignItems="flex-start">
                <ListItemIcon>
                  <Avatar src="/Avatar.svg" />
                </ListItemIcon>
                <ListItemText
                  className={classes.typography}
                  primary={
                    <Typography className={classes.typography}>
                      {message.primary}
                    </Typography>
                  }
                  secondary={
                    <>
                      <Typography
                        className={classes.typography}
                        component="span">
                        {message.detail}
                        <span className={classes.timeText}>
                          {message.secondary}
                        </span>
                      </Typography>
                    </>
                  }
                />
              </ListItem>
            ))}
          </List>
        </CardContent>
      </Card>
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>{t('monitoring.activitiesAddNewTask')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            id="name"
            label="Type"
            type="text"
            fullWidth
            variant="outlined"
          />
          <TextField
            margin="dense"
            id="detail"
            label="Task Detail"
            type="text"
            fullWidth
            multiline
            variant="outlined"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>
            {t('monitoring.activitiesCancelDialog')}
          </Button>
          <Button onClick={handleClose}>
            {t('monitoring.activitiesSubmitDialog')}
          </Button>
        </DialogActions>
      </Dialog>
    </StyledBox>
  );
}
